import { ApiError, getErrorMessage } from '@/lib/api/error';
import type { ComplianceAlert } from '@/services/api/compliance-alerts';
import type { FuturesAssessmentAttemptExplanation, FuturesAssessmentExplanation, FuturesAssessmentOutcome } from '@/services/api/futures-assessment-explanation';
import { isFuturesAppropriateness, type Questionnaire } from '@/services/api/questionnaires';
import { parseJsonIfString } from '@/utils/json';

const hasAnswers = (answers: unknown): boolean => {
  const parsed = parseJsonIfString(answers);

  return typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0;
};

/** Drafts have no result to explain, and result-only broker submissions have no answers */
export const canExplainFuturesAppropriateness = (questionnaire: Questionnaire): questionnaire is Questionnaire & { _meta: { date: string } } =>
  isFuturesAppropriateness(questionnaire) &&
  typeof questionnaire._meta?.date === 'string' &&
  questionnaire.Result !== null &&
  questionnaire.Result !== undefined &&
  hasAnswers(questionnaire.Answers);

/** A manual review alert carries the attempt's stored date and answers, but not its version, so v1 can only be spotted from the explanation */
export const canExplainFuturesReviewAlert = (alert: ComplianceAlert): alert is ComplianceAlert & { date: string } => typeof alert.date === 'string' && hasAnswers(alert.answers);

/**
 * v1's points changed in May-June 2025, after most v1 attempts were scored, so re-scoring wouldn't match the stored result.
 * No v1 attempt stores a version, and the legacy `v` attribute only holds later versions.
 */
export const isV1Attempt = (questionnaire: Questionnaire): boolean => {
  const version = questionnaire.version ?? questionnaire.v;

  return version === undefined || version === null || version === 'v1';
};

export const formatPoints = (points: number, maxPoints: number | null): string => (maxPoints === null ? String(points) : `${points} / ${maxPoints}`);

export const formatThresholds = ({ FAIL, MANUAL_REVIEW }: FuturesAssessmentExplanation['thresholds']): string =>
  `Fail < ${FAIL} · Review ${FAIL}–${MANUAL_REVIEW - 1} · Pass ≥ ${MANUAL_REVIEW}`;

const STORED_STATUS_OUTCOMES: Record<number, FuturesAssessmentOutcome> = { 0: 'PASSED', 1: 'MANUAL-REVIEW', 2: 'FAILED' };

export const formatStoredResult = ({ score, status, manualReview }: FuturesAssessmentAttemptExplanation['stored']): string =>
  `${score}, ${STORED_STATUS_OUTCOMES[status] ?? `status ${status}`}${manualReview ? ' (set by a reviewer)' : ''}`;

const EXPLANATION_ERROR_MESSAGES: Record<string, string> = {
  ASSESSMENT_NOT_FOUND: 'No attempt was found for this date.',
  ASSESSMENT_NOT_SCORED: 'This attempt is an unconfirmed draft, so it has no score to explain.',
  NO_ANSWERS_TO_EXPLAIN: 'This attempt was submitted as a result only, so there are no answers to explain.',
  UNSUPPORTED_VERSION: 'This attempt has no recognised scoring version.',
};

export const getExplanationErrorMessage = (error: unknown): string =>
  (error instanceof ApiError && EXPLANATION_ERROR_MESSAGES[error.message]) || `Failed to explain scoring: ${getErrorMessage(error)}`;
